import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const THEME_STORAGE_KEY = "maple-theme";

const ThemeContext = createContext({
  isDarkMode: false,
  themeMode: "light",
  setThemeMode: () => {},
  toggleTheme: () => {},
  hasUserPreference: false,
});

const getSystemTheme = () => {
  if (typeof window === "undefined" || !window.matchMedia) {
    return "light";
  }

  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

const getStoredTheme = () => {
  if (typeof window === "undefined") {
    return null;
  }

  const saved = localStorage.getItem(THEME_STORAGE_KEY);
  return saved === "light" || saved === "dark" ? saved : null;
};

export const ThemeProvider = ({ children }) => {
  const storedTheme = getStoredTheme();
  const [hasUserPreference, setHasUserPreference] = useState(Boolean(storedTheme));
  const [themeMode, setThemeModeState] = useState(storedTheme || getSystemTheme());

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", themeMode);
  }, [themeMode]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleSystemThemeChange = (event) => {
      if (!hasUserPreference) {
        setThemeModeState(event.matches ? "dark" : "light");
      }
    };

    mediaQuery.addEventListener("change", handleSystemThemeChange);

    return () => {
      mediaQuery.removeEventListener("change", handleSystemThemeChange);
    };
  }, [hasUserPreference]);

  const setThemeMode = useCallback((nextTheme, persist = true) => {
    const normalizedTheme = nextTheme === "dark" ? "dark" : "light";
    setThemeModeState(normalizedTheme);

    if (persist) {
      localStorage.setItem(THEME_STORAGE_KEY, normalizedTheme);
      setHasUserPreference(true);
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeMode(themeMode === "dark" ? "light" : "dark");
  }, [setThemeMode, themeMode]);

  const value = useMemo(
    () => ({
      isDarkMode: themeMode === "dark",
      themeMode,
      setThemeMode,
      toggleTheme,
      hasUserPreference,
    }),
    [themeMode, setThemeMode, toggleTheme, hasUserPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);
